import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";
import type { CognitoUser } from "amazon-cognito-identity-js";

import { type AdminUser, completeNewPassword, getUser, signIn, signOut } from "./auth.js";
import { Login } from "./components/Login.js";

interface AuthContextValue {
  user: AdminUser | null;
  loading: boolean;
  signOut: () => void;
  refresh: () => Promise<void>;
}

const AuthContext = createContext<AuthContextValue | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<AdminUser | null>(null);
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(async () => {
    setUser(await getUser());
  }, []);

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      try {
        const current = await getUser();
        if (!cancelled) setUser(current);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const doSignOut = useCallback(() => {
    signOut();
    setUser(null);
  }, []);

  const value = useMemo(
    () => ({ user, loading, signOut: doSignOut, refresh }),
    [user, loading, doSignOut, refresh],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within AuthProvider");
  return ctx;
}

export function RequireAuth({ children }: { children: ReactNode }) {
  const auth = useAuth();
  const [pendingUser, setPendingUser] = useState<CognitoUser | null>(null);

  const handleSignIn = useCallback(
    async (email: string, password: string) => {
      const result = await signIn(email, password);
      if (result.outcome === "newPasswordRequired") {
        setPendingUser(result.user);
        return;
      }
      await auth.refresh();
    },
    [auth],
  );

  const handleNewPassword = useCallback(
    async (newPassword: string) => {
      if (!pendingUser) return;
      await completeNewPassword(pendingUser, newPassword);
      setPendingUser(null);
      await auth.refresh();
    },
    [auth, pendingUser],
  );

  if (auth.loading) {
    return <p className="muted">Checking session…</p>;
  }
  if (!auth.user) {
    return (
      <Login
        requiresNewPassword={pendingUser !== null}
        onSignIn={handleSignIn}
        onNewPassword={handleNewPassword}
      />
    );
  }
  return <>{children}</>;
}
